import React, { useState } from 'react';
import { Player, PlayerBadgesData } from '../types';
import { BADGES_CONFIG, BadgeItem } from '../config/badgesConfig';
import './PlayerBadges.css';

interface PlayerBadgesProps {
  player: Player;
  isAdmin: boolean;
  onSave: (updatedBadges: PlayerBadgesData) => Promise<void>;
}

const BadgeSlot: React.FC<{
  info: BadgeItem | null;
  onSelect: (id: string) => void;
  onRemove: () => void;
  items: BadgeItem[];
  isAdmin: boolean;
  tooltipText?: string;
  slotLabel: string;
}> = ({ info, onSelect, onRemove, items, isAdmin, tooltipText, slotLabel }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const displayText = tooltipText || info?.name || '';

  return (
    <div className="player-badge-slot">
      {info ? (
        <div
          className="player-badge-item"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={() => setShowTooltip(!showTooltip)}
        >
          <img src={info.image} alt={info.name} />
          {isAdmin && (
            <button type="button" className="player-badge-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Kaldır">×</button>
          )}
          {showTooltip && displayText && <div className="player-badge-tooltip">{displayText}</div>}
        </div>
      ) : isAdmin ? (
        <div className="player-badge-slot-add">
          <button type="button" className="player-badges-add-btn" onClick={() => setShowPicker(!showPicker)} title={`${slotLabel} ekle`}>+</button>
          {showPicker && (
            <>
              <div className="player-badges-overlay" onClick={() => setShowPicker(false)} />
              <div className="player-badges-picker">
                <button type="button" className="player-badges-back" onClick={() => setShowPicker(false)}>← Geri</button>
                {items.length === 0 ? (
                  <p className="player-badges-empty">Henüz eklenmemiş.</p>
                ) : (
                  items.map(item => (
                    <button key={item.id} type="button" className="player-badges-picker-item" onClick={() => { onSelect(item.id); setShowPicker(false); }}>
                      <img src={item.image} alt={item.name} />
                      <span>{item.name}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="player-badge-slot-empty" />
      )}
    </div>
  );
};

export const PlayerBadges: React.FC<PlayerBadgesProps> = ({ player, isAdmin, onSave }) => {
  const [showStylePicker, setShowStylePicker] = useState(false);

  const badges: PlayerBadgesData = player.badges || { styles: [] };
  const { country, league, team, styles = [] } = badges;

  const countryInfo = country ? BADGES_CONFIG.countries.find(c => c.id === country) : null;
  const leagueInfo = league ? BADGES_CONFIG.leagues.find(l => l.id === league) : null;
  const teamInfo = team ? BADGES_CONFIG.teams.find(t => t.id === team) : null;

  const handleSet = async (key: keyof PlayerBadgesData, value: string | string[] | undefined) => {
    await onSave({ ...badges, [key]: value });
  };

  const handleAddStyle = async (id: string) => {
    if (styles.includes(id)) return;
    await onSave({ ...badges, styles: [...styles, id] });
    setShowStylePicker(false);
  };

  const handleRemoveStyle = async (id: string) => {
    await onSave({ ...badges, styles: styles.filter(s => s !== id) });
  };

  return (
    <div className="player-badges">
      {/* Satır 1: Ülke (sol) - Lig (orta) - Takım (sağ) */}
      <div className="player-badges-row player-badges-row-1">
        <BadgeSlot
          info={countryInfo || null}
          slotLabel="Ülke"
          onSelect={(id) => handleSet('country', id)}
          onRemove={() => handleSet('country', undefined)}
          items={BADGES_CONFIG.countries}
          isAdmin={isAdmin}
        />
        <BadgeSlot
          info={leagueInfo || null}
          slotLabel="Lig"
          onSelect={(id) => handleSet('league', id)}
          onRemove={() => handleSet('league', undefined)}
          items={BADGES_CONFIG.leagues}
          isAdmin={isAdmin}
        />
        <BadgeSlot
          info={teamInfo || null}
          slotLabel="Takım"
          onSelect={(id) => handleSet('team', id)}
          onRemove={() => handleSet('team', undefined)}
          items={BADGES_CONFIG.teams}
          isAdmin={isAdmin}
        />
      </div>

      {/* Satır 2: Oyuncu tarzları + tek + butonu */}
      <div className="player-badges-row player-badges-row-2">
        {styles.map(styleId => {
          const styleInfo = BADGES_CONFIG.styles.find(s => s.id === styleId);
          if (!styleInfo) return null;
          return (
            <StyleBadge
              key={styleId}
              info={styleInfo}
              isAdmin={isAdmin}
              onRemove={() => handleRemoveStyle(styleId)}
            />
          );
        })}
        {isAdmin && (
          <div className="player-badges-style-add">
            <button type="button" className="player-badges-add-btn" onClick={() => setShowStylePicker(!showStylePicker)} title="Oyuncu tarzı ekle">+</button>
            {showStylePicker && (
              <>
                <div className="player-badges-overlay" onClick={() => setShowStylePicker(false)} />
                <div className="player-badges-picker player-badges-picker-styles">
                  <button type="button" className="player-badges-back" onClick={() => setShowStylePicker(false)}>← Geri</button>
                  {BADGES_CONFIG.styles.length === 0 ? (
                    <p className="player-badges-empty">Henüz eklenmemiş.</p>
                  ) : (
                    BADGES_CONFIG.styles.filter(s => !styles.includes(s.id)).map(item => (
                      <button key={item.id} type="button" className="player-badges-picker-item" onClick={() => handleAddStyle(item.id)}>
                        <img src={item.image} alt={item.name} />
                        <span>{item.name}</span>
                      </button>
                    ))
                  )}
                  {BADGES_CONFIG.styles.filter(s => !styles.includes(s.id)).length === 0 && BADGES_CONFIG.styles.length > 0 && (
                    <p className="player-badges-empty">Tüm tarzlar eklendi.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StyleBadge: React.FC<{ info: BadgeItem & { description: string }; isAdmin: boolean; onRemove: () => void }> = ({ info, isAdmin, onRemove }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div
      className="player-badge-item"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip(!showTooltip)}
    >
      <img src={info.image} alt={info.name} />
      {isAdmin && <button type="button" className="player-badge-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>}
      {showTooltip && (
        <div className="player-badge-tooltip">
          <span className="player-badge-tooltip-name">{info.name}</span>
          <span className="player-badge-tooltip-desc">{info.description}</span>
        </div>
      )}
    </div>
  );
};
